/**
 * Handles keydown events, allowing only numeric input and control keys.
 * @param event - The keydown event object.
 */
export const handleKeyDown = (event: any) => {
  const keyPressed = event.key;
  const isControlKey = event.ctrlKey || event.metaKey;
  const isAllowedKey = /^\d$/.test(keyPressed) || isControlKey;

  if (keyPressed === 'Tab') {
    return;
  }

  if (!isAllowedKey && keyPressed !== 'Backspace') {
    event.preventDefault();
  }
};

/**
 * Handles keydown events, allowing alphanumeric input and control keys.
 * @param event - The keydown event object.
 */
export const handleKeyDownAlphanumeric = (event: any) => {
  const keyPressed = event.key;
  const isControlKey = event.ctrlKey || event.metaKey;
  const isAllowedKey = /^[a-zA-Z0-9]$/.test(keyPressed) || isControlKey;

  if (keyPressed === 'Tab') {
    return;
  }

  if (!isAllowedKey && keyPressed !== 'Backspace') {
    event.preventDefault();
  }
};

/**
 * Handles keydown events, allowing only letters (incl. ñ/Ñ), spaces,
 * hyphens, and standard navigation/editing keys. Used for name inputs.
 * Works on both plain inputs and MUI Autocomplete's root div.
 * @param event - The keydown event object.
 */
export const handleNameKeyDown = (event: any) => {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Tab',
    'Enter',
    'Home',
    'End',
  ];

  const isLetterOnly = /^[a-zA-ZñÑ\s-]$/.test(event.key);

  if (!isLetterOnly && !allowedKeys.includes(event.key)) {
    event.preventDefault();
  }
};

/**
 * Same as handleNameKeyDown, but also stops Tab from propagating —
 * needed on AutoCompleteController fields so Tab doesn't get intercepted
 * by the autocomplete's internal listbox/focus handling.
 * @param event - The keydown event object.
 */
export const handleNameKeyDownAutocomplete = (event: any) => {
  if (event.key === 'Tab') {
    event.stopPropagation();
    return;
  }
  handleNameKeyDown(event);
};

/**
 * Handles keydown events for phone number inputs, allowing only digits
 * and standard navigation/editing keys (no Ctrl/Meta shortcut allowance).
 * @param event - The keydown event object.
 */
export const handlePhoneNumberKeyDown = (event: any) => {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'Tab',
    'Home',
    'End',
  ];

  if (!/^[0-9]$/.test(event.key) && !allowedKeys.includes(event.key)) {
    event.preventDefault();
  }
};

/**
 * Formats a raw string of digits into PH-style phone format: 0917-123-4567.
 * Strips non-digits and caps at 11 digits.
 * @param value - The raw input string.
 * @returns The formatted phone number string.
 */
export const formatPhoneNumber = (value: string): string => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);

  if (numbers.length <= 4) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;

  return `${numbers.slice(0, 4)}-${numbers.slice(4, 7)}-${numbers.slice(7)}`;
};

/**
 * Handles paste events for name-type inputs — strips out anything that
 * isn't a letter (incl. ñ/Ñ), space, or hyphen, since onKeyDown alone
 * doesn't validate pasted content.
 * @param event - The paste event object.
 */
export const handleNamePaste = (event: any) => {
  event.preventDefault();
  const pastedText = event.clipboardData.getData('text');
  const sanitized = pastedText
    .replace(/[^a-zA-ZñÑ\s-]/g, '')
    .replace(/^\s+/, '');
  document.execCommand('insertText', false, sanitized);
};

/**
 * Handles paste events, preventing non-numeric input.
 * @param event - The paste event object.
 */
export const handlePaste = (event: any) => {
  const pastedText = event.clipboardData.getData('text');

  if (/[a-zA-Z]/.test(pastedText)) {
    event.preventDefault();
  }
};

/**
 * Handles paste events for controlled inputs, preventing non-numeric input.
 * @param e - The paste event object.
 * @param setValue - Function to set the value of the input.
 * @param fieldName - The name of the field being pasted into.
 */
export const handleControllerPaste = (
  e: any,
  setValue: any,
  fieldName: string,
) => {
  const pastedText = e.clipboardData.getData('text');

  if (/[a-zA-Z]/.test(pastedText)) {
    e.preventDefault();
    setValue(fieldName, '');
  }
};

/**
 * Converts a string to camelCase.
 * @param str - The input string.
 * @returns The camelCase version of the input string.
 */
export const handleCamelCase = (str: string): string =>
  str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');

/**
 * Converts a camelCase string to space-separated words with the first letter capitalized.
 * @param str - The input camelCase string.
 * @returns The space-separated string with the first letter capitalized.
 */
export const handleReverseCamelCase = (str: string): string =>
  str
    .replace(/\s+/g, '') // Remove spaces
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toUpperCase() : ` ${word.toLowerCase()}`;
    });

export const handleTitleCamelCase = (
  str: string,
  exceptions: string[] = [],
): string => {
  // Remove spaces first
  const cleaned = str.replace(/\s+/g, '');

  // Check if the string matches any exception (case-insensitive)
  const exceptionMatch = exceptions.find(
    (ex) => cleaned.toLowerCase() === ex.toLowerCase(),
  );
  if (exceptionMatch) return exceptionMatch.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Split camelCase / PascalCase
  return cleaned
    .replace(/([a-z])([A-Z])/g, '$1 $2') // insert space before capital
    .replace(/^./, (c) => c.toUpperCase()); // capitalize first letter
};

/**
 * Creates a delay using a Promise.
 * @param ms - The number of milliseconds to delay.
 * @param callback - Optional callback function to execute after the delay.
 * @returns A Promise that resolves after the specified delay.
 */
export const delay = (ms: number, callback?: () => void) => {
  return new Promise((resolve) =>
    setTimeout(() => {
      callback && callback();
      resolve(null);
    }, ms),
  );
};
